import { useState, type ReactNode, type KeyboardEvent, type MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import Tooltip from '@mui/material/Tooltip';
import { IconArrowDownRight, IconArrowUpRight, IconCheck, IconClock, IconCopy, IconDots, IconMinus, IconX } from '@tabler/icons-react';
import type { Tone } from '@infinix/tokens';
import { Avatar } from './primitives';
import { TwoLine } from './table';
import { Btn, inputSx, type Opt } from './controls';
import { useToast } from './feedback';
import { tint, v } from './format';

/* =========================================================================================
 * Loading and layout
 * ========================================================================================= */

/** Light loading block shown while a card's data loads. */
export function Skeleton({ height = 120 }: { height?: number }) {
  return <Box aria-hidden sx={{ height, borderRadius: '12px', background: v('sunk') }} />;
}

/** Horizontal filter row above a table (wraps on narrow screens). */
export function FilterBar({ children, divider = true }: { children: ReactNode; divider?: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', p: '14px 16px', borderBottom: divider ? `1px solid ${v('border')}` : 0 }}>
      {children}
    </Box>
  );
}

/** Responsive form grid: `cols` columns on desktop, one on phones. */
export function FormGrid({ children, cols = 2 }: { children: ReactNode; cols?: number }) {
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: `repeat(${cols}, minmax(0,1fr))` }, gap: '14px 16px' }}>{children}</Box>;
}

/** Settings row: title and caption on the left, control on the right, hairline between rows. */
export function SettingRow({ title, caption, control, first = false }: { title: ReactNode; caption?: ReactNode; control: ReactNode; first?: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', py: '14px', borderTop: first ? 0 : `1px solid ${v('divider')}`, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: 13.5, fontWeight: 600 }}>{title}</Box>
        {caption && <Box sx={{ fontSize: 12.5, color: v('caption'), mt: '2px' }}>{caption}</Box>}
      </Box>
      <Box sx={{ flexShrink: 0 }}>{control}</Box>
    </Box>
  );
}

/** Small label above a value (drawer sections, detail grids). */
export function LabelValue({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ fontSize: 12, color: v('caption'), fontWeight: 500, mb: '5px' }}>{label}</Box>
      <Box sx={{ fontSize: 13.5, color: v('text') }}>{children}</Box>
    </Box>
  );
}

/* =========================================================================================
 * Display
 * ========================================================================================= */

const toneVar: Record<Tone | 'accent', string> = {
  success: v('success'), info: v('info'), warning: v('warning'), danger: v('danger'), neutral: v('dim'), accent: v('accentText'),
};

/**
 * Small tag. Neutral by default; `tone` tints it (status, accent for roles); `color` tints it with any CSS
 * colour (e.g. a product colour for a business-unit name); `mono` for codes such as scopes and event names.
 */
export function Tag({ children, tone, color, mono = false }: { children: ReactNode; tone?: Tone | 'accent'; color?: string; mono?: boolean }) {
  const c = color ?? (tone ? toneVar[tone] : undefined);
  return (
    <Box
      component="span"
      className={mono ? 'ix-mono' : undefined}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '5px', maxWidth: '100%', height: 22, px: '8px', borderRadius: '6px', fontSize: mono ? 11.5 : 12,
        fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle',
        color: c ?? v('dim'), background: c ? tint(c, 11) : v('sunk'), boxShadow: `0 0 0 1px ${c ? tint(c, 26) : v('border2')} inset`,
      }}
    >
      {children}
    </Box>
  );
}

/** Wrapping row of tags or chips. */
export function TagRow({ children }: { children: ReactNode }) {
  return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>{children}</Box>;
}

/** Square tinted icon holder. */
export function IconTile({ children, tone = 'accent', size = 38 }: { children: ReactNode; tone?: 'accent' | 'success' | 'info' | 'warning' | 'danger'; size?: number }) {
  return (
    <Box aria-hidden sx={{ width: size, height: size, borderRadius: '11px', background: tint(v(tone), 13), color: v(tone === 'accent' ? 'accentText' : tone), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {children}
    </Box>
  );
}

/** "↗ 8.2%" change indicator. Positive is success-coloured; pass `down` for a decline. */
export function Delta({ children, down = false }: { children: ReactNode; down?: boolean }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: 12, fontWeight: 600, color: v(down ? 'danger' : 'success') }}>
      {down ? <IconArrowDownRight size={12} stroke={2.6} aria-hidden /> : <IconArrowUpRight size={12} stroke={2.6} aria-hidden />}
      {children}
    </Box>
  );
}

/** Separator bullet for inline facts ("SmartBill • MetroCare • Robert Jones"). */
export function Dot() {
  return <Box component="span" aria-hidden sx={{ color: v('faint') }}>•</Box>;
}

/** Yes/no with an icon (capabilities, read-only switches). */
export function Flag({ on, label }: { on: boolean; label?: ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 13, color: on ? v('text') : v('caption') }}>
      <Box component="span" sx={{ display: 'flex', color: on ? v('success') : v('faint') }}>
        {on ? <IconCheck size={15} stroke={2.4} aria-hidden /> : <IconMinus size={15} stroke={2.4} aria-hidden />}
      </Box>
      {label ?? (on ? 'Yes' : 'No')}
    </Box>
  );
}

/** Avatar + name + caption (people tables). */
export function PersonCell({ name, initials, sub }: { name: ReactNode; initials: string; sub?: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
      <Avatar initials={initials} />
      <TwoLine top={name} sub={sub} />
    </Box>
  );
}

type CalloutTone = 'success' | 'info' | 'warning' | 'danger';

/** Ringed, tinted callout inside a card (payout returned, connector degraded, retry sent). */
export function Callout({ tone, icon, children }: { tone: CalloutTone; icon: ReactNode; children: ReactNode }) {
  const c = v(tone);
  return (
    <Box
      role={tone === 'danger' ? 'alert' : 'status'}
      sx={{ display: 'flex', gap: '12px', p: '14px 16px', borderRadius: '12px', background: tint(c, 8), boxShadow: `0 0 0 1px ${tint(c, 28)} inset`, fontSize: 13, lineHeight: 1.55, color: v('dim') }}
    >
      <Box aria-hidden sx={{ display: 'flex', flexShrink: 0, color: c, pt: '1px' }}>{icon}</Box>
      <Box sx={{ minWidth: 0, '& strong': { color: v('text') } }}>{children}</Box>
    </Box>
  );
}

/* =========================================================================================
 * Steppers
 * ========================================================================================= */

/** Wizard stepper: numbered steps, done ticks, and jumping back to any step already reached. */
export function Stepper({ steps, active, maxReached = active, onPick, label }: { steps: readonly string[]; active: number; maxReached?: number; onPick?: (i: number) => void; label: string }) {
  return (
    <Box component="ol" aria-label={label} sx={{ listStyle: 'none', m: 0, p: '16px 22px', display: 'flex', gap: { xs: '6px', sm: '10px' }, borderBottom: `1px solid ${v('border')}`, overflowX: 'auto' }}>
      {steps.map((stepLabel, i) => {
        const on = i === active;
        const done = !on && (i < active || i < maxReached);
        const reachable = Boolean(onPick) && i <= maxReached;
        return (
          <Box component="li" key={stepLabel} sx={{ display: 'flex', alignItems: 'center', gap: '10px', flex: i < steps.length - 1 ? 1 : '0 0 auto', minWidth: 0 }}>
            <Box
              component="button"
              type="button"
              disabled={!reachable}
              aria-current={on ? 'step' : undefined}
              onClick={() => onPick?.(i)}
              sx={{
                all: 'unset', display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: reachable ? 'pointer' : 'default', whiteSpace: 'nowrap', borderRadius: '8px',
                '&:focus-visible': { outline: `2px solid ${v('accent')}`, outlineOffset: 2 },
              }}
            >
              <Box sx={{
                width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                color: on ? '#fff' : done ? v('success') : v('caption'),
                background: on ? v('button') : done ? tint(v('success'), 14) : v('sunk'),
                boxShadow: on || done ? 'none' : `0 0 0 1px ${v('border2')} inset`,
              }}>
                {done ? <IconCheck size={13} stroke={3} aria-hidden /> : <span className="ix-mono">{i + 1}</span>}
              </Box>
              <Box component="span" sx={{ fontSize: 13, fontWeight: on ? 700 : 500, color: on ? v('text') : i <= maxReached ? v('dim') : v('caption') }}>{stepLabel}</Box>
            </Box>
            {i < steps.length - 1 && <Box aria-hidden sx={{ flex: 1, height: '1px', minWidth: 12, background: i < maxReached ? tint(v('success'), 50) : v('border2') }} />}
          </Box>
        );
      })}
    </Box>
  );
}

export interface StatusStep { title: ReactNode; meta?: ReactNode; tone: 'success' | 'danger' | 'warning' | 'info'; done: boolean }

/** Horizontal status lifecycle (payout created → sent → returned → retry). */
export function StatusSteps({ steps, label }: { steps: StatusStep[]; label: string }) {
  return (
    <Box component="ol" aria-label={label} sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', overflowX: 'auto', pb: '2px' }}>
      {steps.map((s, i) => {
        const c = v(s.tone);
        const last = i === steps.length - 1;
        const Icon = s.tone === 'success' ? IconCheck : s.tone === 'danger' ? IconX : IconClock;
        return (
          <Box component="li" key={i} aria-current={!s.done ? 'step' : undefined} sx={{ display: 'flex', flex: last ? '0 0 auto' : '1 1 0', minWidth: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 120, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 26, height: 26, borderRadius: '50%', background: tint(c, 15), boxShadow: `0 0 0 1px ${tint(c, 50)} inset`, color: c, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={13} stroke={2.6} aria-hidden />
                </Box>
                {!last && <Box aria-hidden sx={{ flexGrow: 1, height: '2px', mx: '10px', background: s.done ? tint(c, 50) : v('border') }} />}
              </Box>
              <Box sx={{ lineHeight: 1.35, pr: '10px' }}>
                <Box sx={{ fontSize: 13, fontWeight: 600 }}>{s.title}</Box>
                {s.meta && <Box sx={{ fontSize: 12, color: v('caption') }}>{s.meta}</Box>}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/* =========================================================================================
 * Menus and copying
 * ========================================================================================= */

export interface MenuAction { label: string; onClick: () => void; danger?: boolean; disabled?: boolean; title?: string }

/** "…" overflow menu for a table row or card. Clicks and keys never reach the row's own handler. */
export function RowMenu({ label, actions, disabledReason }: { label: string; actions: MenuAction[]; disabledReason?: string | undefined }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const stop = (e: MouseEvent | KeyboardEvent) => e.stopPropagation();
  const disabled = Boolean(disabledReason) || actions.length === 0;
  const button = (
    <IconButton
      aria-label={label}
      aria-haspopup="menu"
      aria-expanded={Boolean(anchor)}
      disabled={disabled}
      size="small"
      onClick={(e) => setAnchor(e.currentTarget)}
      sx={{ width: 30, height: 30, borderRadius: '8px', color: v('dim'), '&:hover': { background: v('sunk') } }}
    >
      <IconDots size={16} aria-hidden />
    </IconButton>
  );
  return (
    <Box component="span" onClick={stop} onKeyDown={stop} sx={{ display: 'inline-flex' }}>
      {disabledReason ? <Tooltip title={disabledReason}><span>{button}</span></Tooltip> : button}
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} slotProps={{ list: { dense: true, sx: { p: '5px', minWidth: 200 } } }}>
        {actions.map((a) => (
          <MenuItem
            key={a.label}
            disabled={a.disabled}
            title={a.title}
            onClick={() => { setAnchor(null); if (!a.disabled) a.onClick(); }}
            sx={{ borderRadius: '7px', fontSize: 13, minHeight: 34, color: a.danger ? v('danger') : v('text') }}
          >
            {a.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

/** Copy text where the browser allows it, then confirm with a toast either way. */
export function useCopy() {
  const toast = useToast();
  return (text: string, message: string) => {
    try {
      void navigator.clipboard?.writeText(text).catch(() => undefined);
    } catch {
      /* clipboard unavailable: the toast still confirms */
    }
    toast(message);
  };
}

/** Mono value (key, secret, URL) with a Copy button. */
export function CopyField({ value, what }: { value: string; what: string }) {
  const copy = useCopy();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '10px 12px', borderRadius: '10px', background: v('sunk'), border: `1px solid ${v('border2')}` }}>
      <Box className="ix-mono" sx={{ flex: 1, minWidth: 0, fontSize: 13, color: v('text'), wordBreak: 'break-all' }}>{value}</Box>
      <Btn icon={<IconCopy size={14} aria-hidden />} onClick={() => copy(value, `${what} copied`)} sx={{ flexShrink: 0, height: 32 }}>Copy</Btn>
    </Box>
  );
}

/* =========================================================================================
 * Form groups
 * ========================================================================================= */

/** "$ [ 1,640.00 ]  matches folio total" */
export function MoneyInput({ id, value, onChange, hint, invalid = false, describedBy }: { id: string; value: string; onChange: (s: string) => void; hint?: ReactNode; invalid?: boolean; describedBy?: string }) {
  return (
    <InputBase
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ inputMode: 'decimal', 'aria-invalid': invalid || undefined, 'aria-describedby': describedBy }}
      className="ix-mono"
      startAdornment={<Box component="span" aria-hidden className="ix-mono" sx={{ color: v('caption'), mr: '6px' }}>$</Box>}
      endAdornment={hint ? <Box component="span" sx={{ fontSize: 12, color: v('caption'), whiteSpace: 'nowrap', ml: 1 }}>{hint}</Box> : undefined}
      sx={{ ...inputSx(invalid), height: 44, px: '14px', fontSize: 15 }}
    />
  );
}

/** Parse "1,640.00" or "$1640" → 1640; NaN when it isn't a money amount. */
export const parseMoney = (s: string) => {
  const t = s.replace(/[$,\s]/g, '');
  return /^\d+(\.\d{1,2})?$/.test(t) ? Number(t) : Number.NaN;
};

const legendSx = { fontSize: 13, fontWeight: 600, color: v('text'), p: 0, mb: '6px' };
const checkSx = { p: '6px', color: v('border2'), '&.Mui-checked': { color: v('button') } };

/** Checkbox group with a real fieldset and legend. Set `mono` on an option for codes (API scopes, event names). */
export function CheckboxGroup({
  legend, options, value, onChange, disabled = false, columns = 1, hint, hideLegend = false,
}: { legend: string; options: readonly (Opt & { hint?: string; mono?: boolean })[]; value: string[]; onChange: (v: string[]) => void; disabled?: boolean; columns?: number; hint?: ReactNode; hideLegend?: boolean }) {
  const toggle = (val: string) => onChange(value.includes(val) ? value.filter((x) => x !== val) : [...value, val]);
  return (
    <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
      <Box component="legend" sx={hideLegend ? { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' } : legendSx}>{legend}</Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: `repeat(${columns}, minmax(0,1fr))` }, gap: '2px 12px' }}>
        {options.map((o) => (
          <Box component="label" key={o.value} sx={{ display: 'flex', alignItems: o.hint ? 'flex-start' : 'center', gap: '4px', cursor: disabled || o.disabled ? 'default' : 'pointer', fontSize: 13.5 }}>
            <Checkbox size="small" checked={value.includes(o.value)} disabled={disabled || o.disabled} onChange={() => toggle(o.value)} sx={checkSx} />
            <Box sx={{ pt: o.hint ? '5px' : 0 }}>
              <Box className={o.mono ? 'ix-mono' : undefined} sx={{ fontSize: o.mono ? 12.5 : 13.5 }}>{o.label}</Box>
              {o.hint && <Box sx={{ fontSize: 12, color: v('caption'), mt: '1px' }}>{o.hint}</Box>}
            </Box>
          </Box>
        ))}
      </Box>
      {hint && <Box sx={{ fontSize: 12, color: v('caption'), mt: '4px' }}>{hint}</Box>}
    </Box>
  );
}

/** Radio choices as bordered cards with captions (policies such as MFA). */
export function RadioCards({ legend, name, options, value, onChange, disabled = false }: { legend: string; name: string; options: readonly (Opt & { hint?: string })[]; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
      <Box component="legend" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>{legend}</Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((o) => {
          const on = o.value === value;
          return (
            <Box
              component="label"
              key={o.value}
              sx={{
                display: 'flex', alignItems: 'flex-start', gap: '6px', p: '10px 12px 10px 6px', borderRadius: '10px', cursor: disabled ? 'default' : 'pointer',
                border: `1px solid ${on ? tint(v('accent'), 50) : v('border')}`, background: on ? tint(v('accent'), 6) : 'transparent',
              }}
            >
              <Radio size="small" name={name} value={o.value} checked={on} disabled={disabled || o.disabled} onChange={() => onChange(o.value)} sx={{ ...checkSx, p: '4px' }} />
              <Box sx={{ pt: '3px' }}>
                <Box sx={{ fontSize: 13.5, fontWeight: 600 }}>{o.label}</Box>
                {o.hint && <Box sx={{ fontSize: 12.5, color: v('caption'), mt: '2px' }}>{o.hint}</Box>}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/** Multi-select as a row of pressable chips (allowed providers, methods, event types). */
export function ChipToggles({ label, options, value, onChange, disabled = false }: { label: string; options: readonly string[]; value: string[]; onChange: (v: string[]) => void; disabled?: boolean }) {
  return (
    <Box role="group" aria-label={label} sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <Box
            key={o}
            component="button"
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', height: 30, px: '11px', borderRadius: '8px', cursor: disabled ? 'default' : 'pointer',
              fontFamily: 'inherit', fontSize: 12.5, fontWeight: on ? 600 : 500, color: on ? v('accentText') : v('dim'), opacity: disabled ? 0.6 : 1,
              background: on ? tint(v('accent'), 12) : v('sunk'), border: `1px solid ${on ? tint(v('accent'), 40) : v('border2')}`,
              '&:focus-visible': { outline: `2px solid ${v('accent')}`, outlineOffset: 2 },
            }}
          >
            {on && <IconCheck size={13} stroke={2.6} aria-hidden />}
            {o}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Explains why a non-Btn control (card, tile, link) is unavailable: the reason shows on hover and on
 * keyboard focus. Renders the child untouched when there is no reason. For buttons use `Btn disabledReason`.
 */
export function DisabledReason({ reason, children, block = false }: { reason: string | undefined; children: ReactNode; block?: boolean }) {
  if (!reason) return <>{children}</>;
  return (
    <Tooltip title={reason}>
      <Box
        component="span"
        tabIndex={0}
        aria-label={reason}
        sx={{ display: block ? 'flex' : 'inline-flex', borderRadius: '14px', cursor: 'not-allowed', '&:focus-visible': { outline: `2px solid ${v('accent')}`, outlineOffset: 2 } }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}
